'use client';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { RegisterSearch } from './RegisterSearch';
import { Modal, ModalBody, ModalFooter } from './Modal';
import { Button } from './Button';
import { Badge } from './Badge';
import { LabelMark, StatusMark } from './StatusMark';
import type { StatusMarkLook, StatusMarkProps } from 'elowen-plugin-ui-kit';
import { HelpTip } from './HelpTip';
import { Spinner } from './states';
import { useOperationFailure } from './Toast';
import { Checkbox } from './Checkbox';
import { interpolate, useTranslation } from '../../lib/i18n';
import { Segmented } from './Segmented';
import { normalizeText } from '../../lib/normalizeText';
import { holdsAnswer, releasedBy, releasing } from '../../lib/saveFeedback';
import { SelectMenu } from './SelectMenu';
import { SettingsRow } from './SettingsSurface';
import { Toggle } from './Toggle';

/** The kind dropdown's "every kind" option. Radix Select reserves the empty string for "no value", so the
 *  option needs a value of its own; it never matches a row's `filters`. */
const ALL_KINDS = '*';

export interface ManageSelectionItem {
  id: string;
  label: string;
  /** Optional muted detail beneath the label, such as a model's effective API. */
  secondaryText?: string;
  /** Grouping key — items sharing a group render under one header.
   *  `''` pins the item to an ungrouped section at the top (no header, no filter chip),
   *  e.g. a "Default" option or a saved id the vocabulary no longer lists. */
  group: string;
  /** Display name for the group header/filter chip (falls back to `group`). */
  groupLabel?: string;
  icon?: ReactNode;
  badges?: { text: string; tone?: 'accent' | 'muted' }[];
  /** Categorical labels; identifiers stay in badges. */
  labels?: { text: string; tone: 'accent' | 'muted' }[];
  /** The row's semantic state; its independent mark stays focusable when the row is disabled. */
  status?: Pick<StatusMarkProps, 'label' | 'tone'> & StatusMarkLook;
  /** Values of the modal's `kindFilter` options this row matches. */
  filters?: string[];
  /** Row cannot be toggled (e.g. built-in tools) — rendered faded with `disabledHint`. */
  disabled?: boolean;
  /** Hover text for a row the user cannot toggle. */
  disabledHint?: string;
}

interface ManageSelectionCommonProps {
  title: string;
  subtitle?: string;
  open: boolean;
  onClose: () => void;
  /** The modal is gone and focus is back on its opener; see `Modal.onClosed`. */
  onClosed?: () => void;
  items: ManageSelectionItem[];
  /** Header-chip and footer count label, e.g. (n) => `${n} models selected`. Defaults to the generic
   *  "{n} selected". */
  countLabel?: (n: number) => string;
  /** Optional icon per group key, shown in the group header and its filter chip. */
  groupIcons?: Record<string, ReactNode>;
  /** Optional single-choice filter over `ManageSelectionItem.filters`, one compact dropdown beside the search
   *  led by "All", independent of the group tabs. Rows without `filters` show only under "All". */
  kindFilter?: { options: { value: string; label: string }[]; label: string };
}

/** How a rejected save is reported, owned here and passed through by every wrapper of the modal. */
export interface ManageSelectionOutcome {
  /** Curated, localized text for a rejected `onSave`; the generic one is used when absent. Throw a
   *  `SaveConflictError` from `onSave` for a stale selection: it is reported as a conflict with the
   *  recoveries it carries and the modal stays open. */
  failureMessage?: (error: unknown) => string;
  /** The domain's authoritative re-read of what `onSave` changes. When the outcome of a save is unknown (a lost
   *  connection) the card offers it as "Reload current": it must really re-read. Absent, the card offers closing
   *  the modal, labelled as that: safe abandonment, no verification. */
  reload?: () => void | Promise<void>;
}

interface ManageSelectionProps extends ManageSelectionCommonProps, ManageSelectionOutcome {
  selected: Set<string>;
  onSave: (next: Set<string>) => void | Promise<void>;
  saving?: boolean;
  /** Shown in the footer instead of the count when nothing is selected. */
  emptySelectionHint?: string;
  /** An exclusive "everything" choice, drawn as a switch above the list. While it is on, the saved
   *  selection is exactly `{ id }` and the rows are inert; switching it off brings back the rows that
   *  were picked before. `id` must not be the id of any row. */
  allOption?: { id: string; label: string; description?: string };
}

const BADGE_TONES = {
  accent: 'border-primary/40 bg-primary/15 text-primary',
  muted: 'border-border bg-muted text-muted-foreground',
} as const;

/** The badge chips of a row, shared with the single-choice dropdown's rows. */
export function ItemBadges({ badges }: { badges: ManageSelectionItem['badges'] }) {
  return badges?.map((b) => (
    <span key={b.text} className={`shrink-0 rounded border px-1.5 py-0.5 text-small ${BADGE_TONES[b.tone ?? 'muted']}`}>
      {b.text}
    </span>
  )) ?? null;
}

/** One row: a checkbox button whose description is read by assistive technology and on hover when the row
 *  is disabled. */
function Row({ item, on, onToggle }: {
  item: ManageSelectionItem;
  on: boolean;
  onToggle: (item: ManageSelectionItem) => void;
}) {
  return <div className="relative">
    <button
      type="button"
      onClick={() => onToggle(item)}
      disabled={item.disabled}
      aria-pressed={on}
      aria-description={item.secondaryText}
      title={item.disabled && !item.status ? item.disabledHint : undefined}
      className={`flex ${item.secondaryText ? 'h-14' : 'h-10'} w-full items-center gap-2.5 rounded-lg border px-3 text-left text-small transition-colors ${
        on ? 'border-primary/50 bg-primary/15 text-foreground' : 'border-border text-foreground hover:bg-accent hover:text-accent-foreground'
      } ${item.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      {item.icon ? <span aria-hidden className="flex shrink-0 items-center">{item.icon}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{item.label}</span>
        {item.secondaryText ? <span aria-hidden className="truncate text-small text-muted-foreground">{item.secondaryText}</span> : null}
      </span>
      <ItemBadges badges={item.badges} />
      {item.labels?.map((label) => <LabelMark key={label.text} tone={label.tone}>{label.text}</LabelMark>)}
      {item.status ? <span aria-hidden className="size-5.5 shrink-0" /> : null}
      <Checkbox checked={on} />
    </button>
    {item.status ? <span className="absolute right-9 top-1/2 -translate-y-1/2">
      <StatusMark {...item.status} label={`${item.label}: ${item.status.label}${item.disabledHint ? `: ${item.disabledHint}` : ''}`} />
    </span> : null}
  </div>;
}

/** Generic "manage selection" modal: search + group filter chips + grouped checkbox rows.
 *  Selection is LOCAL until "Save changes" hands the next set to `onSave`; Cancel/Esc discards. Save is
 *  disabled while the selection equals what the caller passed in: an unchanged save writes nothing.
 *  The modal owns the promise `onSave` returns. A synchronous handoff closes at once and announces nothing
 *  (the caller owns whatever persistence follows); a rejection is reported through the toast seam as ONE
 *  card and the modal stays open with its selection, so the user can retry.
 *  Items with `group: ''` render pinned above the grouped sections (no header, no filter chip).
 *  A single choice is not this modal's job: it is the anchored dropdown {@link RowPicker}. */
export function ManageSelectionModal(props: ManageSelectionProps) {
  // Mount the stateful body only while open so local selection re-seeds from `selected` on every open.
  if (!props.open) return null;
  return <ManageSelectionModalBody {...props} />;
}

function ManageSelectionModalBody(props: ManageSelectionProps) {
  const { title, subtitle, onClose, onClosed } = props;
  // The busy state sits above the surface so the dialog itself can refuse every dismissal while a save runs.
  const [internalSaving, setInternalSaving] = useState(false);
  const busy = (props.saving ?? false) || internalSaving;
  return (
    <Modal title={title} description={subtitle ? <span className="sr-only">{subtitle}</span> : undefined} titleAside={subtitle ? <HelpTip align="left">{subtitle}</HelpTip> : undefined} onClose={onClose} onClosed={onClosed} closeDisabled={busy} size="xl" aria-busy={busy || undefined}>
      <ManageSelectionContent {...props} busy={busy} setSaving={setInternalSaving} />
    </Modal>
  );
}

/** Everything inside the surface, so the failure reporter is called under the surface it reports for. */
function ManageSelectionContent(props: ManageSelectionProps & { busy: boolean; setSaving: (saving: boolean) => void }) {
  const { onClose, items, countLabel, groupIcons, kindFilter, busy, setSaving, selected, emptySelectionHint, allOption } = props;
  const { t } = useTranslation();
  const reportFailure = useOperationFailure();
  const failureKey = `selection:${useId()}`;
  // A conflict or an unconfirmed save holds Save (see `holdsAnswer`) until a recovery of its card has completed.
  const [held, setHeld] = useState(false);
  const [allOn, setAllOn] = useState(() => allOption !== undefined && selected.has(allOption.id));
  const [local, setLocal] = useState<Set<string>>(() => new Set([...selected].filter((id) => id !== allOption?.id)));
  const [query, setQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [kind, setKind] = useState<string | null>(null);

  // Unique groups in first-appearance order — drives the filter chips and the section order.
  // Pinned (group '') items live above the sections and never get a chip.
  const groups = useMemo(() => {
    const seen = new Map<string, string>();
    for (const it of items) if (it.group !== '' && !seen.has(it.group)) seen.set(it.group, it.groupLabel ?? it.group);
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [items]);

  const q = normalizeText(query.trim());
  const matchesKind = (item: ManageSelectionItem) => kind === null || item.filters?.includes(kind) === true;
  // Pinned rows ignore the group filter (they belong to no group) but still honor the search and the kind.
  const pinned = items.filter((it) => it.group === '' && matchesKind(it) && (!q || normalizeText(it.label).includes(q)));
  const visible = items.filter((it) =>
    it.group !== ''
    && matchesKind(it)
    && (!groupFilter || it.group === groupFilter)
    && (!q || normalizeText(it.label).includes(q) || normalizeText(it.groupLabel ?? it.group).includes(q)));

  const toggle = (item: ManageSelectionItem) => {
    if (busy || item.disabled) return;
    setLocal((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
  };

  const countText = countLabel ?? ((n: number) => interpolate(t.managePicker.selectedCount, { n }));
  // While the all option is on, every row is covered by it, so none of them is a choice.
  const inert = (item: ManageSelectionItem): ManageSelectionItem =>
    allOn && allOption ? { ...item, disabled: true, disabledHint: allOption.label }
      : busy ? { ...item, disabled: true } : item;

  const nextSelection = (): Set<string> => allOn && allOption ? new Set([allOption.id]) : new Set(local);
  // The accepted baseline is what the caller passed in; saving it back is not a change.
  const unchanged = (() => {
    const next = nextSelection();
    return next.size === selected.size && [...next].every((id) => selected.has(id));
  })();

  const save = async () => {
    if (busy || unchanged || held) return;
    setSaving(true);
    try {
      const result = props.onSave(nextSelection());
      // Synchronous pickers should close in the same interaction frame. Async persistence still
      // keeps the modal open until it resolves so failures remain retryable.
      if (result) await result;
      onClose();
    } catch (error) {
      // A lost connection leaves the save unconfirmed: the domain's `reload` re-reads it; without one the only
      // honest offer is closing, which verifies nothing. Nothing resends the save.
      const message = props.failureMessage?.(error) ?? t.common.saveFailed;
      const release = () => setHeld(false);
      setHeld(holdsAnswer(error));
      const reported = releasedBy(error, release);
      if (props.reload) reportFailure(failureKey, reported, { message, reload: releasing(() => props.reload?.(), release) });
      else reportFailure(failureKey, reported, { message, leave: () => onClose() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ModalBody gap={4} fill>
        {allOption ? (
          <div className="@container/field-group shrink-0 rounded-lg border border-border px-3 py-2" data-all-option>
            <SettingsRow
              label={allOption.label}
              hint={allOption.description}
              control={<Toggle checked={allOn} onChange={setAllOn} disabled={busy} label={allOption.label} />}
            />
          </div>
        ) : null}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <RegisterSearch value={query} onChange={setQuery} placeholder={t.managePicker.searchPlaceholder} />
          {kindFilter && kindFilter.options.length > 0 ? (
            <SelectMenu
              label={kindFilter.label}
              className="order-3 w-full shrink-0 sm:order-2 sm:w-28"
              value={kind ?? ALL_KINDS}
              onChange={(next) => setKind(next === ALL_KINDS ? null : next)}
              options={[{ value: ALL_KINDS, label: t.managePicker.all }, ...kindFilter.options]}
            />
          ) : null}
          <Badge tone="muted" className="order-2 shrink-0 sm:order-3">
            {allOn && allOption ? allOption.label : interpolate(t.managePicker.selectedCount, { n: local.size })}
          </Badge>
        </div>

        {groups.length > 1 && (
          <Segmented
            aria-label={t.managePicker.filterByGroup}
            size="sm"
            nowrap
            options={[
              { value: '', label: t.managePicker.all },
              ...groups.map((group) => ({ value: group.id, label: group.label, iconContent: groupIcons?.[group.id] })),
            ]}
            value={groupFilter ?? ''}
            onChange={(value) => setGroupFilter(value || null)}
          />
        )}

        {/* Only the list scrolls; the switch, search and tabs above stay in view. The negative margin and equal
            padding keep a row's focus ring from being cut by the scroll box. */}
        <div className="-m-1 min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
          {pinned.length === 0 && visible.length === 0
            ? <p className="py-6 text-center text-meta italic text-muted-foreground">{t.managePicker.noResults}</p>
            : (
              <div className="flex flex-col gap-4">
                {pinned.length > 0 && (
                  <ul className="flex flex-col gap-1">
                    {pinned.map((item) => <li key={item.id}><Row item={inert(item)} on={local.has(item.id)} onToggle={toggle} /></li>)}
                  </ul>
                )}
                {groups.map((g) => {
                  const groupItems = visible.filter((it) => it.group === g.id);
                  if (groupItems.length === 0) return null;
                  return (
                    <section key={g.id} className="flex flex-col gap-1.5">
                      <h3 className="inline-flex items-center gap-1.5 px-0.5 text-meta text-muted-foreground">
                        {groupIcons?.[g.id]}
                        {g.label}
                      </h3>
                      <ul className="flex flex-col gap-1">
                        {groupItems.map((item) => (
                          <li key={item.id}><Row item={inert(item)} on={local.has(item.id)} onToggle={toggle} /></li>
                        ))}
                      </ul>
                    </section>
                  );
                })}
              </div>
            )}
        </div>
      </ModalBody>
      <ModalFooter
        status={
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="text-meta text-muted-foreground">
              {allOn && allOption
                ? allOption.label
                : local.size === 0 && emptySelectionHint
                  ? emptySelectionHint
                  : countText(local.size)}
            </span>
          </div>
        }
      >
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>{t.common.cancel}</Button>
        <Button type="button" variant="accent" onClick={() => void save()} disabled={busy || unchanged || held}>
          {busy ? <Spinner tone="text-current" /> : null}
          {t.managePicker.saveChanges}
        </Button>
      </ModalFooter>
    </>
  );
}
